import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, Polyline, Marker, Polygon, InfoWindow, HeatmapLayer } from '@react-google-maps/api';
import * as h3 from 'h3-js';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { geoJsonToGoogleMapsPath } from '../../utils/geoJsonUtils';

const defaultCenter = { lat: 40.7, lng: -73.9 };
const defaultZoom = 10;

/**
 * Normalize stop to { lat, lng, is_depot }. Backend may send lat/lng or latitude/longitude.
 * is_depot is used to render depot stops with a different marker color.
 */
function normalizeStops(stops) {
    if (!Array.isArray(stops) || stops.length === 0) return [];
    return stops.map((s) => ({
        ...s,
        lat: Number(s.lat ?? s.latitude ?? 0),
        lng: Number(s.lng ?? s.longitude ?? s.lon ?? 0),
        is_depot: Boolean(s.is_depot),
    })).filter((p) => p.lat !== 0 || p.lng !== 0);
}

function normalizeRecommendedDepots(depots) {
    if (!Array.isArray(depots) || depots.length === 0) return [];
    return depots
        .map((depot, index) => ({
            ...depot,
            id: depot?.id ?? `recommended-depot-${index}`,
            lat: Number(depot?.lat ?? depot?.latitude ?? 0),
            lng: Number(depot?.lng ?? depot?.longitude ?? depot?.lon ?? 0),
            title: depot?.name ?? depot?.label ?? `Recommended depot ${index + 1}`,
        }))
        .filter((point) => point.lat !== 0 || point.lng !== 0);
}

/**
 * Normalize charging station to { lat, lng, title }. API may send latitude/longitude, lat/lng, or GeoJSON feature.
 */
function normalizeChargingStations(stations) {
    if (!Array.isArray(stations) || stations.length === 0) return [];
    return stations
        .map((s) => {
            const coords = s.geometry?.coordinates; // GeoJSON [lng, lat]
            const lat = coords ? Number(coords[1]) : Number(s.lat ?? s.latitude ?? 0);
            const lng = coords ? Number(coords[0]) : Number(s.lng ?? s.longitude ?? s.lon ?? 0);
            const props = s.properties ?? s;
            const title = props.station_name ?? props.name ?? s.station_name ?? s.name ?? 'Charger';
            return { lat, lng, title };
        })
        .filter((p) => p.lat !== 0 || p.lng !== 0);
}

function normalizeMdhdInfrastructure(stations) {
    if (!Array.isArray(stations) || stations.length === 0) return [];
    return stations
        .map((station) => ({
            lat: Number(station?.lat ?? 0),
            lng: Number(station?.lon ?? 0),
            title: station?.name ?? station?.owner ?? station?.addr ?? 'MDHD infrastructure',
        }))
        .filter((point) => point.lat !== 0 || point.lng !== 0);
}

function normalizeTruckStops(stops) {
    if (!Array.isArray(stops) || stops.length === 0) return [];
    return stops
        .map((stop) => ({
            lat: Number(stop?.latitude ?? 0),
            lng: Number(stop?.longitude ?? 0),
            title: stop?.name ?? stop?.address ?? stop?.street ?? stop?.city ?? 'Truck stop',
            type: stop?.type ?? 'truck_stop',
        }))
        .filter((point) => point.lat !== 0 || point.lng !== 0);
}

function normalizeStopProfileMarkers(profiles) {
    if (!Array.isArray(profiles) || profiles.length === 0) return [];
    return profiles
        .map((profile) => ({
            ...profile,
            id: profile?.id,
            lat: Number(profile?.latitude ?? 0),
            lng: Number(profile?.longitude ?? 0),
            title: `Charging stop ${profile?.stop_index ?? ''}`,
            energyDeliveredKwh: Number(profile?.energy_delivered_kwh ?? 0),
        }))
        .filter((point) => point.id != null && (point.lat !== 0 || point.lng !== 0));
}

function normalizeWarehousePolygons(warehouses) {
    if (!Array.isArray(warehouses) || warehouses.length === 0) return [];
    return warehouses.flatMap((warehouse, index) => {
        const geometry = warehouse?.geometry;
        if (!geometry?.coordinates) return [];
        const paths = geoJsonToGoogleMapsPath(geometry.coordinates, geometry.type || 'Polygon');
        const buildPolygonEntry = (key, polygonPath) => {
            const center = Array.isArray(polygonPath) && polygonPath.length > 0
                ? polygonPath.reduce(
                    (acc, point) => ({
                        lat: acc.lat + Number(point?.lat || 0),
                        lng: acc.lng + Number(point?.lng || 0),
                        count: acc.count + 1,
                    }),
                    { lat: 0, lng: 0, count: 0 },
                )
                : null;
            return {
                key,
                paths: polygonPath,
                center: center && center.count > 0
                    ? { lat: center.lat / center.count, lng: center.lng / center.count }
                    : null,
                title: warehouse?.properties?.place_name || warehouse?.place_name || warehouse?.properties?.warehouse_class || warehouse?.warehouse_class || 'Warehouse',
            };
        };
        if ((geometry.type || 'Polygon') === 'MultiPolygon') {
            return (Array.isArray(paths) ? paths : []).map((polygonPath, polygonIndex) => ({
                ...buildPolygonEntry(`warehouse-${index}-${polygonIndex}`, polygonPath),
            }));
        }
        return Array.isArray(paths) && paths.length > 0 ? [buildPolygonEntry(`warehouse-${index}`, paths)] : [];
    });
}

const HEATMAP_COLORS = ['#d9f99d', '#86efac', '#4ade80', '#22c55e', '#15803d', '#14532d'];
const HEATMAP_METRIC_CONFIG = {
    energy_delivered: {
        legendTitle: 'Annual total load (MWh)',
        unitLabel: 'MWh',
        transformForLegend: (value) => value / 1000.0,
        transformFromLegend: (value) => value * 1000.0,
    },
    peak_power_kw: {
        legendTitle: 'Annual peak load (MW)',
        unitLabel: 'MW',
        transformForLegend: (value) => value / 1000.0,
        transformFromLegend: (value) => value * 1000.0,
    },
};

function _niceStep(value) {
    if (!Number.isFinite(value) || value <= 0) return 1;
    const exponent = Math.floor(Math.log10(value));
    const magnitude = 10 ** exponent;
    const residual = value / magnitude;
    if (residual <= 1) return 1 * magnitude;
    if (residual <= 2) return 2 * magnitude;
    if (residual <= 5) return 5 * magnitude;
    return 10 * magnitude;
}

function getHeatmapMetricValue(hex, metricKey) {
    if (metricKey === 'peak_power_kw') return Number(hex?.peak_power_kw ?? 0);
    return Number(hex?.energy_delivered ?? 0);
}

function isValidLatLng(point) {
    const lat = Number(point?.lat);
    const lng = Number(point?.lng);
    return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

function getPointExtent(points) {
    const validPoints = (points || []).filter(isValidLatLng);
    if (validPoints.length === 0) return null;
    let minLat = validPoints[0].lat;
    let maxLat = validPoints[0].lat;
    let minLng = validPoints[0].lng;
    let maxLng = validPoints[0].lng;
    for (const point of validPoints) {
        minLat = Math.min(minLat, point.lat);
        maxLat = Math.max(maxLat, point.lat);
        minLng = Math.min(minLng, point.lng);
        maxLng = Math.max(maxLng, point.lng);
    }
    return {
        validPoints,
        latSpan: maxLat - minLat,
        lngSpan: maxLng - minLng,
    };
}

function chooseFitPoints({ path, utilityBoundaryPoints, heatmapCenters }) {
    const routePoints = (path || []).filter(isValidLatLng);
    if (routePoints.length > 0) return routePoints;

    const boundaryExtent = getPointExtent(utilityBoundaryPoints);
    const heatmapExtent = getPointExtent(heatmapCenters);

    if (boundaryExtent?.validPoints?.length) {
        const boundaryLooksTooWide = boundaryExtent.latSpan > 25 || boundaryExtent.lngSpan > 40;
        if (!boundaryLooksTooWide || !heatmapExtent?.validPoints?.length) {
            return boundaryExtent.validPoints;
        }
    }

    if (heatmapExtent?.validPoints?.length) {
        return heatmapExtent.validPoints;
    }

    return boundaryExtent?.validPoints || [];
}

/** Color by selected heatmap metric using explicit legend thresholds. */
function getColorByValue(value, thresholds) {
    if (!Array.isArray(thresholds) || thresholds.length === 0) return HEATMAP_COLORS[0];
    const numericValue = Number(value || 0);
    if (numericValue <= 0) return HEATMAP_COLORS[0];
    for (let i = 0; i < thresholds.length; i += 1) {
        if (numericValue <= thresholds[i]) {
            return HEATMAP_COLORS[Math.min(i, HEATMAP_COLORS.length - 1)];
        }
    }
    return HEATMAP_COLORS[HEATMAP_COLORS.length - 1];
}

function HexProfileMiniChart({ values = [], color = '#2563eb' }) {
    const safeValues = Array.isArray(values) ? values.slice(0, 24).map((v) => Number(v || 0)) : [];
    if (safeValues.length === 0) {
        return <div className="text-xs text-gray-500">No hourly profile values.</div>;
    }
    const width = 320;
    const height = 170;
    const padLeft = 58;
    const padRight = 18;
    const padTop = 10;
    const padBottom = 36;
    const innerW = width - (padLeft + padRight);
    const innerH = height - (padTop + padBottom);
    const maxVal = Math.max(...safeValues, 1e-9);
    const yTicks = [0, maxVal / 2, maxVal];
    const roundedTickValue = (value) => Math.round(Number(value || 0) / 100) * 100;
    const xTickHours = [0, 6, 12, 18, 23];
    const xForHour = (hour) => padLeft + (innerW * hour) / 23;
    const yForValue = (value) => padTop + innerH - ((value / maxVal) * innerH);
    const points = safeValues.map((value, idx) => {
        const x = xForHour(idx);
        const y = yForValue(value);
        return `${x},${y}`;
    }).join(' ');
    return (
        <div>
            <svg width={width} height={height} role="img" aria-label="Hexagon hourly load profile">
                {yTicks.map((tickValue, idx) => {
                    const y = yForValue(tickValue);
                    return (
                        <g key={`y-tick-${idx}`}>
                            <line x1={padLeft} y1={y} x2={width - padRight} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                            <line x1={padLeft - 4} y1={y} x2={padLeft} y2={y} stroke="#9ca3af" strokeWidth="1" />
                            <text x={padLeft - 8} y={y + 3} textAnchor="end" className="fill-gray-500 text-[10px]">
                                {roundedTickValue(tickValue).toLocaleString()}
                            </text>
                        </g>
                    );
                })}
                {xTickHours.map((hour) => {
                    const x = xForHour(hour);
                    return (
                        <g key={`x-tick-${hour}`}>
                            <line x1={x} y1={padTop + innerH} x2={x} y2={padTop + innerH + 4} stroke="#9ca3af" strokeWidth="1" />
                            <text x={x} y={padTop + innerH + 16} textAnchor="middle" className="fill-gray-500 text-[10px]">
                                {`${String(hour).padStart(2, '0')}:00`}
                            </text>
                        </g>
                    );
                })}
                <line x1={padLeft} y1={padTop + innerH} x2={width - padRight} y2={padTop + innerH} stroke="#9ca3af" strokeWidth="1" />
                <line x1={padLeft} y1={padTop} x2={padLeft} y2={padTop + innerH} stroke="#9ca3af" strokeWidth="1" />
                <polyline fill="none" stroke={color} strokeWidth="2" points={points} />
                <circle cx={xForHour(0)} cy={yForValue(safeValues[0])} r="2" fill={color} />
                <circle cx={xForHour(safeValues.length - 1)} cy={yForValue(safeValues[safeValues.length - 1])} r="2" fill={color} />
                <text
                    x={16}
                    y={padTop + (innerH / 2)}
                    transform={`rotate(-90, 16, ${padTop + (innerH / 2)})`}
                    textAnchor="middle"
                    className="fill-gray-500 text-[10px]"
                >
                    Hourly load (kWh)
                </text>
                <text
                    x={padLeft + (innerW / 2)}
                    y={height - 4}
                    textAnchor="middle"
                    className="fill-gray-500 text-[10px]"
                >
                    Hour of day
                </text>
            </svg>
        </div>
    );
}

export function StopChainMap({
    stops,
    selectedStopIndex = 0,
    onStopClick = null,
    recommendedDepots = [],
    displayMode = 'precise',
    routeOverlayPaths = [],
    showRouteOverlay = false,
    showPath = true,
    showLabels = true,
    showStopLegend = false,
    showLayerLegend = false,
    emptyMessage = 'No map data available.',
    chargingStopProfiles = [],
    selectedStopProfileId = null,
    onStopProfileClick = null,
    chargingStations = [],
    showChargerMarkers = false,
    mdhdInfrastructure = [],
    showMdhdInfrastructure = false,
    warehouses = [],
    showWarehouses = false,
    truckStops = [],
    showTruckStops = false,
    hexLoadAggregate = [],
    showLoadHeatmap = false,
    loadHeatmapMetric = 'energy_delivered',
    heatmapMaxValueOverride = null,
    onHexagonClick = null,
    onClearHexagonSelection = null,
    selectedHexagonH3Index = null,
    selectedHexagonData = null,
    selectedForecastYear = null,
    showHexagonProfilePopup = false,
    utilityBoundaryGeometry = null,
    showUtilityBoundary = false,
    className = '',
}) {
    const { isLoaded, loadError } = useGoogleMaps();
    const path = useMemo(() => normalizeStops(stops), [stops]);
    const recommendedDepotPoints = useMemo(
        () => normalizeRecommendedDepots(recommendedDepots),
        [recommendedDepots]
    );
    const chargerPoints = useMemo(() => normalizeChargingStations(chargingStations), [chargingStations]);
    const mdhdInfrastructurePoints = useMemo(() => normalizeMdhdInfrastructure(mdhdInfrastructure), [mdhdInfrastructure]);
    const truckStopPoints = useMemo(() => normalizeTruckStops(truckStops), [truckStops]);
    const stopProfilePoints = useMemo(() => normalizeStopProfileMarkers(chargingStopProfiles), [chargingStopProfiles]);
    const warehousePolygons = useMemo(() => normalizeWarehousePolygons(warehouses), [warehouses]);
    const utilityBoundaryPaths = useMemo(() => {
        if (!showUtilityBoundary || !utilityBoundaryGeometry?.coordinates) return [];
        const geometryType = utilityBoundaryGeometry?.type || 'Polygon';
        const paths = geoJsonToGoogleMapsPath(utilityBoundaryGeometry.coordinates, geometryType);
        if (geometryType === 'MultiPolygon') return Array.isArray(paths) ? paths : [];
        return Array.isArray(paths) && paths.length > 0 ? [paths] : [];
    }, [showUtilityBoundary, utilityBoundaryGeometry]);
    const utilityBoundaryPoints = useMemo(() => utilityBoundaryPaths.flat(), [utilityBoundaryPaths]);
    const heatmapCenters = useMemo(() => {
        if (!showLoadHeatmap || !Array.isArray(hexLoadAggregate) || hexLoadAggregate.length === 0) return [];
        return hexLoadAggregate
            .map((hex) => {
                try {
                    const [lat, lng] = h3.cellToLatLng(hex.h3_index);
                    return { lat, lng };
                } catch {
                    return null;
                }
            })
            .filter(Boolean);
    }, [hexLoadAggregate, showLoadHeatmap]);
    const overlayPaths = useMemo(
        () => (Array.isArray(routeOverlayPaths) ? routeOverlayPaths : [])
            .map((overlayPath, index) => ({
                id: overlayPath?.id ?? `overlay-${index}`,
                label: overlayPath?.label ?? '',
                pathSource: overlayPath?.path_source ?? 'routed',
                path: Array.isArray(overlayPath?.path) ? overlayPath.path.filter(isValidLatLng) : [],
            }))
            .filter((overlayPath) => overlayPath.path.length > 1),
        [routeOverlayPaths]
    );
    const isCloudMode = displayMode === 'cloud';
    const primaryFitPath = useMemo(() => {
        if (path.length > 0) return path;
        if (stopProfilePoints.length > 0) return stopProfilePoints;
        if (recommendedDepotPoints.length > 0) return recommendedDepotPoints;
        if (overlayPaths.length > 0) return overlayPaths.flatMap((overlayPath) => overlayPath.path);
        return [];
    }, [overlayPaths, path, recommendedDepotPoints, stopProfilePoints]);
    const fitPoints = useMemo(
        () => chooseFitPoints({ path: primaryFitPath, utilityBoundaryPoints, heatmapCenters }),
        [primaryFitPath, utilityBoundaryPoints, heatmapCenters],
    );
    const center = useMemo(() => {
        const points = fitPoints;
        if (points.length === 0) return defaultCenter;
        const sum = points.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
        return { lat: sum.lat / points.length, lng: sum.lng / points.length };
    }, [fitPoints]);
    const mapRef = useRef(null);
    const onLoad = useCallback((map) => {
        mapRef.current = map;
        if (fitPoints.length > 0 && window.google?.maps) {
            const b = new window.google.maps.LatLngBounds();
            fitPoints.forEach((p) => b.extend(p));
            map.fitBounds(b, { top: 24, right: 24, bottom: 24, left: 24 });
        }
    }, [fitPoints]);
    const onUnmount = useCallback(() => {
        mapRef.current = null;
    }, []);

    // When path changes (e.g. user selected a different row), fit bounds again
    useEffect(() => {
        if (fitPoints.length > 0 && mapRef.current && window.google?.maps) {
            const b = new window.google.maps.LatLngBounds();
            fitPoints.forEach((p) => b.extend(p));
            mapRef.current.fitBounds(b, { top: 24, right: 24, bottom: 24, left: 24 });
        }
    }, [fitPoints]);

    const metricConfig = HEATMAP_METRIC_CONFIG[loadHeatmapMetric] || HEATMAP_METRIC_CONFIG.energy_delivered;
    const hasRoute = path.length > 0;
    const hasRecommendedDepots = recommendedDepotPoints.length > 0;
    const cloudHeatmapData = useMemo(() => {
        if (!isCloudMode || !hasRoute || !window.google?.maps?.LatLng) return [];
        return path.map((point) => {
            const rawDurationSeconds = Number(point?.stop_duration ?? point?.duration_seconds ?? point?.stop_duration_seconds ?? 0);
            const baseWeight = rawDurationSeconds > 0 ? Math.max(1, rawDurationSeconds / 1800) : 1;
            return {
                location: new window.google.maps.LatLng(point.lat, point.lng),
                weight: point.is_depot ? baseWeight * 0.35 : baseWeight,
            };
        });
    }, [hasRoute, isCloudMode, path]);
    const cloudHeatmapOptions = useMemo(() => ({
        dissipating: true,
        radius: 28,
        opacity: 0.8,
        gradient: [
            'rgba(0, 0, 0, 0)',
            'rgba(96, 165, 250, 0.28)',
            'rgba(59, 130, 246, 0.45)',
            'rgba(37, 99, 235, 0.65)',
            'rgba(147, 51, 234, 0.75)',
            'rgba(244, 63, 94, 0.9)',
        ],
    }), []);
    const overlayLegendText = useMemo(() => {
        if (!showRouteOverlay || overlayPaths.length === 0) return '';
        const pathSources = new Set(overlayPaths.map((overlayPath) => overlayPath.pathSource));
        if (pathSources.has('fuzzy')) {
            return 'Fuzzy route overlay highlights likely corridors inferred from stop patterns and nearby primary roads.';
        }
        if (pathSources.has('approximate')) {
            return 'Route overlay shows routed paths where available and straight-line fallbacks where routing was unavailable.';
        }
        return 'Route overlay shows routed geometry between stop sequences.';
    }, [overlayPaths, showRouteOverlay]);
    const getOverlayPolylineOptions = useCallback((overlayPath) => {
        switch (overlayPath?.pathSource) {
            case 'fuzzy':
                return {
                    strokeColor: '#7c3aed',
                    strokeOpacity: 0.5,
                    strokeWeight: 8,
                    geodesic: true,
                    zIndex: 2,
                };
            case 'approximate':
                return {
                    strokeColor: '#f59e0b',
                    strokeOpacity: 0.85,
                    strokeWeight: 4,
                    icons: window.google?.maps?.SymbolPath
                        ? [{
                            icon: {
                                path: 'M 0,-1 0,1',
                                strokeOpacity: 1,
                                strokeWeight: 2,
                                scale: 3,
                            },
                            offset: '0',
                            repeat: '12px',
                        }]
                        : undefined,
                    zIndex: 3,
                };
            default:
                return {
                    strokeColor: '#2563eb',
                    strokeOpacity: 0.75,
                    strokeWeight: 4,
                    zIndex: 3,
                };
        }
    }, []);

    // H3 load heatmap polygons using selected metric.
    const heatmapScale = useMemo(() => {
        if (!showLoadHeatmap || !Array.isArray(hexLoadAggregate) || hexLoadAggregate.length === 0) return null;
        const values = hexLoadAggregate.map((hex) => getHeatmapMetricValue(hex, loadHeatmapMetric));
        const computedMaxValue = Math.max(...values, 1);
        const normalizedOverride = Number(heatmapMaxValueOverride);
        const maxValue = Number.isFinite(normalizedOverride) && normalizedOverride > 0
            ? normalizedOverride
            : computedMaxValue;
        const maxLegendValue = metricConfig.transformForLegend(maxValue);
        const rawStepLegendValue = maxLegendValue / HEATMAP_COLORS.length;
        const stepLegendValue = _niceStep(rawStepLegendValue);
        const thresholdsLegend = HEATMAP_COLORS.map((_, idx) => (idx + 1) * stepLegendValue);
        const thresholds = thresholdsLegend.map((v) => metricConfig.transformFromLegend(v));
        const levels = thresholdsLegend.map((maxBin, idx) => {
            const minBin = idx === 0 ? 0 : thresholdsLegend[idx - 1];
            return {
                color: HEATMAP_COLORS[idx],
                label: `${minBin.toLocaleString(undefined, { maximumFractionDigits: 2 })} - ${maxBin.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${metricConfig.unitLabel}`,
            };
        });
        return {
            thresholds,
            levels,
            legendTitle: metricConfig.legendTitle,
        };
    }, [hexLoadAggregate, showLoadHeatmap, loadHeatmapMetric, metricConfig, heatmapMaxValueOverride]);

    const loadHeatmapPolygons = useMemo(() => {
        if (!showLoadHeatmap || !Array.isArray(hexLoadAggregate) || hexLoadAggregate.length === 0) return [];
        const thresholds = heatmapScale?.thresholds ?? [];
        return hexLoadAggregate.map((hex, i) => {
            const h3Index = hex.h3_index;
            const metricValue = getHeatmapMetricValue(hex, loadHeatmapMetric);
            if (!h3Index) return null;
            try {
                const boundary = h3.cellToBoundary(h3Index, true);
                const path = boundary.map(([lng, lat]) => ({ lat, lng }));
                const isSelectedHex = String(selectedHexagonH3Index || '') === String(h3Index);
                return (
                    <Polygon
                        key={h3Index || i}
                        paths={path}
                        onClick={onHexagonClick ? () => onHexagonClick(hex) : undefined}
                        options={{
                            fillColor: getColorByValue(metricValue, thresholds),
                            fillOpacity: isSelectedHex ? 0.9 : 0.78,
                            strokeColor: isSelectedHex ? '#dc2626' : '#052e16',
                            strokeOpacity: isSelectedHex ? 1 : 0.92,
                            strokeWeight: isSelectedHex ? 6 : 1.4,
                            clickable: Boolean(onHexagonClick),
                            zIndex: isSelectedHex ? 20 : 1,
                        }}
                    />
                );
            } catch {
                return null;
            }
        }).filter(Boolean);
    }, [hexLoadAggregate, showLoadHeatmap, heatmapScale, onHexagonClick, selectedHexagonH3Index, loadHeatmapMetric]);
    const selectedHexagonPopup = useMemo(() => {
        if (!showHexagonProfilePopup || !selectedHexagonH3Index || !Array.isArray(hexLoadAggregate)) return null;
        const selectedHex = selectedHexagonData
            || hexLoadAggregate.find((row) => String(row?.h3_index) === String(selectedHexagonH3Index))
            || null;
        if (!selectedHex?.h3_index) return null;
        try {
            const [lat, lng] = h3.cellToLatLng(selectedHex.h3_index);
            const yearlyProfile = Array.isArray(selectedHex?.hourly_load_profile_kwh) ? selectedHex.hourly_load_profile_kwh : [];
            const dailyProfile = yearlyProfile.slice(0, 24).map((value) => Number(value || 0) / 365.0);
            return {
                position: { lat, lng },
                hex: selectedHex,
                profile: dailyProfile,
            };
        } catch {
            return null;
        }
    }, [showHexagonProfilePopup, selectedHexagonH3Index, selectedHexagonData, hexLoadAggregate]);

    // Container must have explicit dimensions for Google Maps to render
    const containerStyle = useMemo(() => ({
        width: '100%',
        height: '100%',
    }), []);

    const wrapperStyle = {
        width: '100%',
        height: '100%',
    };

    if (loadError) {
        return (
            <div style={wrapperStyle} className={`flex items-center justify-center bg-gray-100 text-gray-600 ${className}`}>
                Map failed to load.
            </div>
        );
    }
    if (!isLoaded) {
        return (
            <div style={wrapperStyle} className={`flex items-center justify-center bg-gray-100 text-gray-600 ${className}`}>
                Loading map...
            </div>
        );
    }
    const hasHeatmapOnly = showLoadHeatmap && Array.isArray(hexLoadAggregate) && hexLoadAggregate.length > 0;
    const hasUtilityBoundary = showUtilityBoundary && utilityBoundaryPaths.length > 0;
    const hasLayerData = (showChargerMarkers && chargerPoints.length > 0)
        || (showMdhdInfrastructure && mdhdInfrastructurePoints.length > 0)
        || (showTruckStops && truckStopPoints.length > 0)
        || (showWarehouses && warehousePolygons.length > 0)
        || stopProfilePoints.length > 0
        || hasRecommendedDepots
        || overlayPaths.length > 0;
    if (!hasRoute && !hasHeatmapOnly && !hasUtilityBoundary && !hasLayerData) {
        return (
            <div style={wrapperStyle} className={`flex items-center justify-center bg-gray-100 text-gray-500 text-sm ${className}`}>
                {emptyMessage}
            </div>
        );
    }

    return (
        <div className={className} style={{ ...wrapperStyle, position: 'relative' }}>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={defaultZoom}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{ zoomControl: true, streetViewControl: false, mapTypeControl: true, fullscreenControl: true }}
            >
                {utilityBoundaryPaths.map((polygonPath, index) => (
                    <Polygon
                        key={`utility-boundary-${index}`}
                        paths={polygonPath}
                        options={{
                            fillColor: '#2563eb',
                            fillOpacity: 0.08,
                            strokeColor: '#1d4ed8',
                            strokeOpacity: 0.95,
                            strokeWeight: 2.2,
                            clickable: false,
                            zIndex: 0,
                        }}
                    />
                ))}
                {hasRoute && (
                    <>
                        {!isCloudMode && showPath && path.length > 1 && (
                            <Polyline
                                path={path}
                                options={{
                                    strokeColor: '#2563eb',
                                    strokeOpacity: 0.9,
                                    strokeWeight: 4,
                                }}
                            />
                        )}
                        {!isCloudMode && path.map((p, i) => (
                    <Marker
                        key={`stop-${i}`}
                        position={{ lat: p.lat, lng: p.lng }}
                        label={showLabels ? {
                            text: String(i + 1),
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold',
                        } : undefined}
                        icon={
                            p.is_depot && typeof window !== 'undefined' && window.google?.maps?.SymbolPath
                                ? {
                                      path: window.google.maps.SymbolPath.CIRCLE,
                                      scale: 14,
                                      fillColor: '#3bb143',
                                      fillOpacity: 0.95,
                                      strokeColor: '#0b6623',
                                      strokeWeight: 2,
                                  }
                                : undefined
                        }
                        title={p.is_depot ? `Stop ${i + 1} (depot)` : `Stop ${i + 1}`}
                        onClick={onStopClick ? () => onStopClick(i, p) : undefined}
                        zIndex={selectedStopIndex === i ? 10 : 1}
                    />
                ))}
                    </>
                )}
                {isCloudMode && cloudHeatmapData.length > 0 && (
                    <HeatmapLayer data={cloudHeatmapData} options={cloudHeatmapOptions} />
                )}
                {showRouteOverlay && overlayPaths.map((overlayPath) => (
                    <Polyline
                        key={overlayPath.id}
                        path={overlayPath.path}
                        options={getOverlayPolylineOptions(overlayPath)}
                    />
                ))}
                {recommendedDepotPoints.map((point, index) => (
                    <Marker
                        key={`recommended-depot-${point.id}`}
                        position={{ lat: point.lat, lng: point.lng }}
                        title={point.title}
                        label={!isCloudMode && showLabels ? {
                            text: `D${index + 1}`,
                            color: 'white',
                            fontSize: '11px',
                            fontWeight: 'bold',
                        } : undefined}
                        icon={
                            typeof window !== 'undefined' && window.google?.maps?.SymbolPath
                                ? {
                                    path: window.google.maps.SymbolPath.CIRCLE,
                                    scale: 12,
                                    fillColor: '#a855f7',
                                    fillOpacity: 0.95,
                                    strokeColor: '#6b21a8',
                                    strokeWeight: 2,
                                }
                                : undefined
                        }
                        zIndex={6}
                    />
                ))}
                {loadHeatmapPolygons}
                {selectedHexagonPopup && (
                    <InfoWindow
                        position={selectedHexagonPopup.position}
                        onCloseClick={() => {
                            if (typeof onClearHexagonSelection === 'function') onClearHexagonSelection();
                        }}
                    >
                        <div className="w-[320px]">
                            <div className="text-xs font-semibold text-gray-800 mb-1">
                                Hexagon profile{selectedForecastYear ? ` (${selectedForecastYear})` : ''}
                            </div>
                            {/* <div className="text-[11px] text-gray-600 mb-2 break-all">
                                H3 {selectedHexagonPopup.hex.h3_index}
                            </div> */}
                            <div className="text-[12px] text-gray-700 mb-2 flex items-center gap-3">
                                <span>Annual total energy load: {Number(selectedHexagonPopup.hex.energy_delivered / 1000 || 0).toFixed(2)} MWh</span>
                                <span>Yearly peak: {Number(selectedHexagonPopup.hex.peak_power_kw / 1000 || 0).toFixed(2)} MW</span>
                            </div>
                            <HexProfileMiniChart
                                values={selectedHexagonPopup.profile}
                                color={loadHeatmapMetric === 'peak_power_kw' ? '#7c3aed' : '#2563eb'}
                            />
                        </div>
                    </InfoWindow>
                )}
                {showWarehouses && warehousePolygons.map((warehouse) => (
                    <React.Fragment key={warehouse.key}>
                        <Polygon
                            paths={warehouse.paths}
                            options={{
                                fillColor: '#f59e0b',
                                fillOpacity: 0.24,
                                strokeColor: '#d97706',
                                strokeOpacity: 1,
                                strokeWeight: 1.8,
                                clickable: false,
                                zIndex: 2,
                            }}
                        />
                        {warehouse.center && (
                            <Marker
                                position={warehouse.center}
                                title={warehouse.title}
                                icon={
                                    typeof window !== 'undefined' && window.google?.maps?.SymbolPath
                                        ? {
                                              path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                                              scale: 4,
                                              rotation: 45,
                                              fillColor: '#f59e0b',
                                              fillOpacity: 0.95,
                                              strokeColor: '#92400e',
                                              strokeWeight: 1.2,
                                          }
                                        : undefined
                                }
                                zIndex={3}
                            />
                        )}
                    </React.Fragment>
                ))}
                {showChargerMarkers && chargerPoints.length > 0 && chargerPoints.map((point, i) => (
                    <Marker
                        key={`charger-${i}`}
                        position={{ lat: point.lat, lng: point.lng }}
                        title={point.title}
                        icon={
                            typeof window !== 'undefined' && window.google?.maps?.SymbolPath
                                ? {
                                      path: window.google.maps.SymbolPath.CIRCLE,
                                      scale: 7,
                                      fillColor: '#22c55e',
                                      fillOpacity: 0.9,
                                      strokeColor: '#166534',
                                      strokeWeight: 1.5,
                                  }
                                : undefined
                        }
                        zIndex={2}
                    />
                ))}
                {stopProfilePoints.map((point) => {
                    const isSelected = String(selectedStopProfileId || '') === String(point.id);
                    return (
                        <Marker
                            key={`stop-profile-${point.id}`}
                            position={{ lat: point.lat, lng: point.lng }}
                            title={`${point.title} | ${point.energyDeliveredKwh.toFixed(1)} kWh`}
                            onClick={onStopProfileClick ? () => onStopProfileClick(point) : undefined}
                            icon={
                                typeof window !== 'undefined' && window.google?.maps?.SymbolPath
                                    ? {
                                          path: window.google.maps.SymbolPath.CIRCLE,
                                          scale: isSelected ? 11 : 8,
                                          fillColor: isSelected ? '#f97316' : '#fb923c',
                                          fillOpacity: 0.95,
                                          strokeColor: isSelected ? '#7c2d12' : '#9a3412',
                                          strokeWeight: isSelected ? 2.2 : 1.5,
                                      }
                                    : undefined
                            }
                            zIndex={isSelected ? 12 : 4}
                        />
                    );
                })}
                {showMdhdInfrastructure && mdhdInfrastructurePoints.length > 0 && mdhdInfrastructurePoints.map((point, i) => (
                    <Marker
                        key={`mdhd-infrastructure-${i}`}
                        position={{ lat: point.lat, lng: point.lng }}
                        title={point.title}
                        icon={
                            typeof window !== 'undefined' && window.google?.maps?.SymbolPath
                                ? {
                                      path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                                      scale: 5,
                                      fillColor: '#8b5cf6',
                                      fillOpacity: 0.95,
                                      strokeColor: '#6d28d9',
                                      strokeWeight: 1.2,
                                  }
                                : undefined
                        }
                        zIndex={2}
                    />
                ))}
                {showTruckStops && truckStopPoints.length > 0 && truckStopPoints.map((point, i) => (
                    <Marker
                        key={`truck-stop-${i}`}
                        position={{ lat: point.lat, lng: point.lng }}
                        title={point.title}
                        icon={
                            typeof window !== 'undefined' && window.google?.maps?.SymbolPath
                                ? {
                                      path: window.google.maps.SymbolPath.CIRCLE,
                                      scale: 6,
                                      fillColor: point.type === 'service_center' ? '#ef4444' : '#f97316',
                                      fillOpacity: 0.95,
                                      strokeColor: '#7c2d12',
                                      strokeWeight: 1.2,
                                  }
                                : undefined
                        }
                        zIndex={2}
                    />
                ))}
            </GoogleMap>
            {showLayerLegend && (
                <div className="absolute right-4 top-4 z-10 w-[340px] max-w-[min(340px,calc(100vw-40px))] space-y-3 rounded-2xl border border-slate-200/90 bg-white/95 p-4 text-slate-800 shadow-xl backdrop-blur-md">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Map Legend</div>
                        <div className="mt-1 text-sm font-semibold text-slate-900">Layers & Visual Keys</div>
                    </div>
                    {showStopLegend && !isCloudMode && (hasRoute || hasRecommendedDepots) && (
                        <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Stop Symbols</div>
                            <div className="flex items-center gap-2 text-xs text-slate-700">
                                <span className="inline-block h-3.5 w-3.5 rounded-full border border-red-700 bg-red-600" />
                                <span>Red: non-depot dwelling point</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-700">
                                <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-green-800 bg-green-500" />
                                <span>Green: depot dwelling point</span>
                            </div>
                            {hasRecommendedDepots ? (
                                <div className="flex items-center gap-2 text-xs text-slate-700">
                                    <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-purple-800 bg-purple-500" />
                                    <span>Purple: recommended depot</span>
                                </div>
                            ) : null}
                        </div>
                    )}
                    {(heatmapScale || (isCloudMode && hasRoute) || (showRouteOverlay && overlayLegendText)) && (
                        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3">
                            {heatmapScale ? (
                                <div className="space-y-1.5">
                                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Load Heatmap</div>
                                    {heatmapScale.levels.map((lvl, idx) => (
                                        <div key={`embedded-legend-${idx}`} className="flex items-center gap-2 text-[11px] text-slate-600">
                                            <span
                                                className="inline-block h-3.5 w-3.5 rounded-sm border border-slate-300"
                                                style={{ backgroundColor: lvl.color }}
                                            />
                                            <span>{lvl.label}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            {isCloudMode && hasRoute ? (
                                <div className={`space-y-1 text-[11px] text-slate-600 ${heatmapScale ? 'border-t border-slate-100 pt-3' : ''}`}>
                                    <div>Cloud mode shows dwelling intensity as a blurred surface.</div>
                                    <div>Longer stops contribute more intensity; depot stops are down-weighted.</div>
                                </div>
                            ) : null}
                            {showRouteOverlay && overlayLegendText ? (
                                <div className={`text-[11px] text-slate-600 ${heatmapScale || (isCloudMode && hasRoute) ? 'border-t border-slate-100 pt-3' : ''}`}>
                                    {overlayLegendText}
                                </div>
                            ) : null}
                        </div>
                    )}
                </div>
            )}
            {heatmapScale && !showLayerLegend && (
                <div className="absolute bottom-4 right-4 z-10 rounded-lg border border-gray-200 bg-white/95 shadow-md p-3 text-xs">
                    <div className="font-semibold text-gray-800 mb-2">{heatmapScale.legendTitle || 'Load heatmap legend'}</div>
                    <div className="space-y-1.5">
                        {heatmapScale.levels.map((lvl, idx) => (
                            <div key={`legend-${idx}`} className="flex items-center gap-2">
                                <span
                                    className="inline-block h-3.5 w-3.5 rounded-sm border border-gray-400"
                                    style={{ backgroundColor: lvl.color }}
                                />
                                <span className="text-gray-700">{lvl.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {isCloudMode && hasRoute && !showLayerLegend && (
                <div className="absolute bottom-4 left-4 z-10 rounded-lg border border-gray-200 bg-white/95 shadow-md p-3 text-xs">
                    <div className="font-semibold text-gray-800 mb-2">Cloud mode</div>
                    <div className="space-y-1.5 text-gray-700">
                        <div>Blurred heatmap of dwelling activity.</div>
                        <div>Longer stops contribute more intensity.</div>
                        <div>Depot stops are intentionally down-weighted.</div>
                        {showRouteOverlay && overlayLegendText ? <div>{overlayLegendText}</div> : null}
                    </div>
                </div>
            )}
            {showStopLegend && !showLayerLegend && (hasRoute || hasRecommendedDepots) && !isCloudMode && (
                <div className="absolute bottom-4 left-4 z-10 rounded-lg border border-gray-200 bg-white/95 shadow-md p-3 text-xs">
                    <div className="font-semibold text-gray-800 mb-2">Stop legend</div>
                    <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                            <span className="inline-block h-3.5 w-3.5 rounded-full border border-red-700 bg-red-600" />
                            <span className="text-gray-700">Red: non-depot dwelling point</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-green-800 bg-green-500" />
                            <span className="text-gray-700">Green: depot dwelling point</span>
                        </div>
                        {hasRecommendedDepots ? (
                            <div className="flex items-center gap-2">
                                <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-purple-800 bg-purple-500" />
                                <span className="text-gray-700">Purple: recommended depot</span>
                            </div>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
}